import type { FC } from 'react';
import React, { useState, useRef, useCallback } from 'react';

import { VolumeQuiteIcon } from '../../assets/VolumeQuiteIcon';
import { VolumeLoudIcon } from '../../assets/VolumeLoudIcon';
import { VolumeMutedIcon } from '../../assets/VolumeMutedIcon';
import { ICON_SIZE_MAP } from '../../../constants';
import { Popover } from '../Popover/Popover';
import { VolumeSlider } from '../VolumeSlider/VolumeSlider';
import styles from './VolumeControl.module.scss';

type VolumeControlProps = {
  size: 'medium' | 'large';
  volume: number;
  isMuted: boolean;
  onMuteToggle: () => void;
  onVolumeChange: (volume: number) => void;
};

type VolumeIconProps = {
  volume: number;
  size: number;
  hasVolume: boolean;
};

const VolumeIcon: FC<VolumeIconProps> = ({ volume, hasVolume, size }) => {
  if (!hasVolume) {
    return <VolumeMutedIcon aria-hidden="true" width={size} height={size} />;
  }

  if (volume <= 0.5) {
    return <VolumeQuiteIcon aria-hidden="true" width={size} height={size} />;
  }

  return <VolumeLoudIcon aria-hidden="true" width={size} height={size} />;
};

export const VolumeControl: FC<VolumeControlProps> = ({
  size,
  volume,
  isMuted,
  onMuteToggle,
  onVolumeChange,
}) => {
  const volumeButtonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const hasVolume = !isMuted && volume > 0;
  const iconSize = ICON_SIZE_MAP[size];
  const ariaLabel = hasVolume ? 'Mute video' : 'Unmute video';

  const handlePopoverClose = useCallback(() => {
    setIsOpen(false);
    volumeButtonRef.current?.focus();
  }, [volumeButtonRef]);

  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      if (isMuted && newVolume > 0) {
        onMuteToggle();
      }

      onVolumeChange(newVolume);
    },
    [isMuted, onMuteToggle, onVolumeChange],
  );

  const handlePopoverOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handlePopoverCloseOnLeave = useCallback(() => {
    setIsOpen(false);
  }, []);

  const volumeButton = (
    <button
      ref={volumeButtonRef}
      className={styles.button}
      type="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={!hasVolume}
      onClick={onMuteToggle}
      onFocus={handlePopoverOpen}
      onBlur={handlePopoverCloseOnLeave}
    >
      <VolumeIcon volume={volume} hasVolume={hasVolume} size={iconSize} />
    </button>
  );

  return (
    <Popover
      triggerRef={volumeButtonRef}
      trigger={volumeButton}
      isOpen={isOpen}
      onClose={handlePopoverClose}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverCloseOnLeave}
    >
      <div className={styles.sliderContainer}>
        <VolumeSlider
          volume={isMuted ? 0 : volume}
          onVolumeChange={handleVolumeChange}
        />
      </div>
    </Popover>
  );
};
